import React from 'react';

type IconProps = {
  color?: string;
  size?: number;
  className?: string;
};

function SvgIcon({ paths, color = 'var(--home-accent)', size = 18, className }: IconProps & { paths: string[] }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ color }}
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

const ACCENT = 'var(--home-accent)';
const MUTED = 'oklch(0.55 0.01 260)';

export const LoadIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M3 7h11v9H3z', 'M14 10h3l3 3v3h-6z']} />
);

export const FuelIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 4h10v16H4z', 'M14 8h2l3 3v6a1 1 0 0 1-1 1h-1', 'M17 13a1.5 1.5 0 1 1 0 3']} />
);

export const CashIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 2v20', 'M17 6.5c0-1.5-2-2.5-5-2.5s-5 1.2-5 3 2 2.5 5 3 5 1.2 5 3-2 3-5 3-5-1-5-2.5']} />
);

export const DocIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M6 3h9l3 3v15H6z', 'M9 9h6', 'M9 13h6', 'M9 17h4']} />
);

export const BarsIcon = ({ color = ACCENT, size = 15 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M5 19V13', 'M11 19V8', 'M17 19V15']} />
);

export const PlusIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 5v14', 'M5 12h14']} />
);

export const ReleaseIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M9 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4z', 'M4 20V6h9l3 3v11']} />
);

export const AgreementsIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M6 3h9l3 3v15H6z', 'M9 9h6', 'M9 13h6']} />
);

export const PhoneIcon = ({ color = ACCENT, size = 22 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5v3a1 1 0 0 1-1 1c-8 0-13-5-13-13a1 1 0 0 1 1-1z']}
    className="phone-icon"
  />
);

export const InfoIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 8h.01', 'M11 12h1v5h1']} />
);

export const HelpDeskIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M4 14v-2a8 8 0 1 1 16 0v2',
      'M4 14a2 2 0 0 1 2-2h1v6H6a2 2 0 0 1-2-2z',
      'M20 14a2 2 0 0 0-2-2h-1v6h1a2 2 0 0 0 2-2z',
    ]}
  />
);

/** Nav / chrome icons */
export const HomeNavIcon = ({ color = ACCENT, size = 22 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 11l8-6 8 6', 'M6 10v10h12V10']} />
);

export const TripNavIcon = ({ color = ACCENT, size = 22 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z',
      'M12 11.5a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z',
    ]}
  />
);

export const BellIcon = ({ color = 'currentColor', size = 20 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M6 8a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5H4.5S6 12 6 8z', 'M10 19a2 2 0 0 0 4 0']}
  />
);

export const InboxIcon = ({ color = 'currentColor', size = 20 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 6h16v12H4z', 'M4 6l8 6 8-6']} />
);

export const SettingsIcon = ({ color = 'currentColor', size = 20 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
      'M19.4 13a7.6 7.6 0 0 0 0-2l2-1.6-2-3.4-2.4.7a7.6 7.6 0 0 0-1.7-1L15 3h-6l-.3 2.7a7.6 7.6 0 0 0-1.7 1l-2.4-.7-2 3.4L4.6 11a7.6 7.6 0 0 0 0 2l-2 1.6 2 3.4 2.4-.7a7.6 7.6 0 0 0 1.7 1L9 21h6l.3-2.7a7.6 7.6 0 0 0 1.7-1l2.4.7 2-3.4z',
    ]}
  />
);

export const ChevronLeft = ({ color = ACCENT, size = 22 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M15 6l-6 6 6 6']}
  />
);

export const CloseIcon = ({ color = '#1a1a1a', size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M7 7l10 10', 'M17 7L7 17']} />
);

export const ChevronRight = ({ color = MUTED }: { color?: string }) => (
  <svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true">
    <path d="M1 1L6 6L1 11" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronDown = ({ color = MUTED, size = 7 }: { color?: string; size?: number }) => (
  <svg width={size} height={size * (12 / 7)} viewBox="0 0 7 12" fill="none" aria-hidden="true">
    <path d="M1 1L6 6L1 11" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Load screen option icons */
export const HistoryIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 8v4l3 2', 'M12 21a9 9 0 1 0-9-9']} />
);

export const PreplanIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 3v12', 'M8 11l4 4 4-4', 'M5 21h14']} />
);

export const TrailerIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M3 7h11v9H3z',
      'M14 10h3l3 3v3h-6z',
      'M6.5 19a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z',
      'M16.5 19a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z',
    ]}
  />
);

/** Trip / Documentation screen icons */
export const FolderDocIcon = ({ color = ACCENT, size = 32 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M8 13l2.5 2.5L16 10']}
  />
);

export const PassengerIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M17 8a2.5 2.5 0 1 0-5 0v2H6a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h1']} />
);

export const SeatedIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 3a9 9 0 1 0 9 9', 'M12 3v9l6 3']} />
);

export const BreakdownIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 15c2-4 4-6 8-6s6 2 8 6', 'M6 15v3', 'M18 15v3', 'M9 12V9', 'M15 12V9']} />
);

export const TarpIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M3 16l9-9 9 9', 'M3 16h18v3H3z']} />
);

export const PaperworkIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M17 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
      'M9 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
      'M3 4h2l2.4 12.4a1 1 0 0 0 1 .8h9.2a1 1 0 0 0 1-.8L20 8H6',
      'M17 8v4',
      'M15 10h4',
    ]}
  />
);

export const CabIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 12h4l2-3h4l2 3h4', 'M3 12v5h18v-5', 'M8 17v2', 'M16 17v2']} />
);

export const PlateIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M4 8a2 2 0 0 1 2-2h2l1-2h6l1 2h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
      'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    ]}
  />
);

export const VehicleIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11', 'M3 11h18v6H3z', 'M7 17v2', 'M17 17v2']}
  />
);

export const ScanCornersIcon = ({ color = '#E0453C', size = 20 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M4 4h4', 'M20 4h-4', 'M4 20h4', 'M20 20h-4', 'M4 4v4', 'M20 4v4', 'M4 20v-4', 'M20 20v-4', 'M8 12h8']}
  />
);

/** Pay hub icons */
export const PaycheckIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 6h16v12H4z', 'M8 10h8', 'M8 14h5']} />
);

export const ReferralIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M16 11a3 3 0 1 0-3-3', 'M8 14a3 3 0 1 0-3-3', 'M13 21v-2a3 3 0 0 0-3-3H6', 'M21 21v-2a3 3 0 0 0-2-2.8']}
  />
);

export const FaqIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M12 21a9 9 0 1 0-9-9', 'M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1.5 1-1.5 2.2', 'M12 17h.01']}
  />
);

/** Docs hub icons */
export const SafetyIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z', 'M9 12l2 2 4-4']} />
);

export const ClimateIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M12 3v3', 'M12 18v3', 'M3 12h3', 'M18 12h3', 'M5.6 5.6l2.1 2.1', 'M16.3 16.3l2.1 2.1', 'M5.6 18.4l2.1-2.1', 'M16.3 7.7l2.1-2.1', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z']}
  />
);

export const MaintenanceIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 0 5.4-5.4l-2.1 2.1-1.9-1.9z']}
  />
);

export const BenefitsIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.5-7 10-7 10z']} />
);

export const TrophyIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M8 21h8',
      'M12 17v4',
      'M7 4h10v3a5 5 0 0 1-10 0V4z',
      'M5 4H3v1a4 4 0 0 0 4 4',
      'M19 4h2v1a4 4 0 0 1-4 4',
    ]}
  />
);

export const BookIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2z', 'M6 3v16']} />
);

export const ExpressLaneIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 7h12', 'M4 12h16', 'M4 17h10', 'M18 5l3 2-3 2']} />
);

export const AccidentsIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M12 3l9 16H3z', 'M12 10v4', 'M12 17h.01']}
  />
);

/** Inbox compose FAB */
export const ComposeIcon = ({ color = '#ffffff', size = 15 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 20h16', 'M14 4l6 6-9 9H5v-6z']} />
);

export const MessageIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 5h16v12H8l-4 4z']} />
);

/** Inbox thread send */
export const SendIcon = ({ color = '#ffffff', size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M22 2L11 13', 'M22 2l-7 20-4-9-9-4 20-7z']} />
);

/** Settings */
export const LogoutIcon = ({ color = '#B23B34', size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9']} />
);

export const WalkthroughIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={['M4 19V6a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z', 'M9 12l2 2 4-4']}
  />
);

/** Photo submit pages */
export const CameraIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M4 8a2 2 0 0 1 2-2h2l1-2h6l1 2h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
      'M12 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    ]}
  />
);

export const TrashIcon = ({ color = '#B23B34', size = 18 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M4 7h16', 'M9 7V5h6v2', 'M6 7l1 12h10l1-12', 'M10 11v5', 'M14 11v5']} />
);

export const CheckCircleIcon = ({ color = ACCENT, size = 22 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 21a9 9 0 1 0-9-9', 'M8.5 12l2.5 2.5L16 9.5']} />
);

export const XCircleIcon = ({ color = '#B23B34', size = 22 }: IconProps) => (
  <SvgIcon color={color} size={size} paths={['M12 21a9 9 0 1 0-9-9', 'M9 9l6 6', 'M15 9l-6 6']} />
);

export const CalendarIcon = ({ color = ACCENT, size = 18 }: IconProps) => (
  <SvgIcon
    color={color}
    size={size}
    paths={[
      'M7 3v3',
      'M17 3v3',
      'M4 7h16',
      'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
      'M4 11h16',
    ]}
  />
);


